/**
 * 「运行与安全 A」页面共用的控件与格式化函数（G6 等其他页面也可以直接引用）。
 *
 * 导出清单：
 *   控件
 *     - DateTimeField      Alice 的 24 小时制日期时间选择器（按钮 .log-datetime-field，弹层 .log-datetime-popup）。
 *                          值是浏览器本地时间 'YYYY-MM-DDTHH:mm:ss'，空串表示未选；要发给后端时用 localToIso() 转成 UTC ISO。
 *     - AliceModal         Alice 的弹窗外壳（.modal-mask > .modal），portal 到 body、Esc 关闭、可选点遮罩关闭。
 *     - ExportDialog       导出对话框：起止日期、最大条数、可选的「带正文」勾选，提交时显示进行中状态并把错误交回调用方。
 *     - ChartCanvas        Chart.js 画布：配置变化时销毁重建（与 Alice 一致），卸载时销毁。
 *     - SearchInput        带放大镜的筛选输入框（回车查询，输入法组合中的回车不触发）。
 *   时间
 *     - localToIso / isoToLocal / dayStartIso / dayEndIso / todayLocal / addDays / localOffsetMinutes
 *   格式
 *     - fmtDuration（队列用：ms / s / m s）、fmtLatency（日志用）、fmtTokens（K/M）、fmtPct、fmtInt
 *     - useMoney（金额：整数 unit 转花瓣与美元，未知值显示破折号，不当 0）
 *   其他
 *     - useInterval        带清理的定时器（delay 为 null 时停止）
 *
 * 背景：Alice 的这些控件散落在 gateway_admin.html 的脚本里（_openLogDateTimePicker、showExportDialog 等），
 *   请求日志、生图记录、搜索记录都要用，视频、音频记录以后也可能用。
 * 设计意图：类名与 Alice 一致，样式用 alice-admin.css 里已有的 .log-datetime-*、.log-dtp-*、.modal-*；
 *   日期时间一律按浏览器本地时区理解，换成 UTC ISO 才发给后端（Alice 写死北京时间，本仓运营人员在不同时区）。
 * 关键约束：不改 kit.tsx 与 kit-runtime2.tsx 的既有导出；弹层与弹窗都 portal 到 body，避免被表格容器裁掉。
 */
import { IconCalendar, IconSearch } from '@tabler/icons-react'
import Chart from 'chart.js/auto'
import type { ChartConfiguration } from 'chart.js'
import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type InputHTMLAttributes,
  type ReactNode,
} from 'react'
import { createPortal } from 'react-dom'
import { fmtPetals, fmtUsd } from '@ap/api-client/money'
import { AG_runtime as G } from '@ap/i18n/admin-runtime'
import { intlTag, useI18n } from '@ap/i18n'
import { fieldStyle } from './kit-runtime2.tsx'

// ------------------------------------------------------------------ 时间

const pad = (n: number) => String(n).padStart(2, '0')

/** 本地时间的 'YYYY-MM-DD'。 */
export function dateKey(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

export function todayLocal(): string {
  return dateKey(new Date())
}

/** 日期字符串加减天数（按本地日历）。 */
export function addDays(date: string, days: number): string {
  const [y, m, d] = date.split('-').map(Number)
  return dateKey(new Date(y, m - 1, d + days))
}

/** 浏览器与 UTC 的偏移（分钟，东区为正），后端按它划分「自然日」。 */
export function localOffsetMinutes(): number {
  return -new Date().getTimezoneOffset()
}

/** 本地 'YYYY-MM-DDTHH:mm:ss'（或带空格）转 UTC ISO；空串或非法值返回 undefined。 */
export function localToIso(value: string): string | undefined {
  const m = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?$/.exec(value.trim())
  if (!m) return undefined
  const [, y, mo, d, h, mi, s] = m
  const t = new Date(Number(y), Number(mo) - 1, Number(d), Number(h), Number(mi), Number(s ?? 0))
  return Number.isNaN(t.getTime()) ? undefined : t.toISOString()
}

/** UTC ISO 转本地 'YYYY-MM-DDTHH:mm:ss'。 */
export function isoToLocal(iso: string): string {
  const t = new Date(iso)
  return `${dateKey(t)}T${pad(t.getHours())}:${pad(t.getMinutes())}:${pad(t.getSeconds())}`
}

/** 本地某天 00:00:00 的 UTC ISO；空串返回 undefined。 */
export function dayStartIso(date: string): string | undefined {
  return date ? localToIso(`${date}T00:00:00`) : undefined
}

/** 本地某天 23:59:59 的 UTC ISO（截止时间双侧包含）；空串返回 undefined。 */
export function dayEndIso(date: string): string | undefined {
  return date ? localToIso(`${date}T23:59:59`) : undefined
}

// ------------------------------------------------------------------ 格式

export function fmtInt(n: number | null | undefined): string {
  return Number(n ?? 0).toLocaleString()
}

/** 推理队列的时长：不足 1 秒显示毫秒，不足 1 分钟显示秒，其余显示「分 秒」。 */
export function fmtDuration(ms: number | null | undefined): string {
  const n = Number(ms ?? 0)
  if (n < 1000) return `${Math.max(0, Math.round(n))}ms`
  if (n < 60000) return `${(n / 1000).toFixed(1)}s`
  return `${Math.floor(n / 60000)}m ${Math.floor((n % 60000) / 1000)}s`
}

/** 日志里的时延：不足 1 秒显示毫秒，其余显示秒；没有值显示破折号。 */
export function fmtLatency(ms: number | null | undefined, big = false): string {
  if (ms === null || ms === undefined) return '—'
  if (ms < 1000) return `${ms}ms`
  return `${(ms / 1000).toFixed(big && ms >= 10000 ? 0 : 1)}s`
}

export function fmtTokens(n: number | null | undefined): string {
  const v = Number(n ?? 0)
  if (!v) return '0'
  if (v >= 1e6) return `${(v / 1e6).toFixed(1)}M`
  if (v >= 1e3) return `${(v / 1e3).toFixed(1)}K`
  return String(v)
}

export function fmtPct(v: number | null | undefined, digits = 1): string {
  return v === null || v === undefined || !Number.isFinite(v) ? '—' : `${(v * 100).toFixed(digits)}%`
}

/**
 * 金额展示：后端给整数 unit（1e-8 美元），用户看到花瓣，运营对账要看美元。
 * 约束：null 表示成本未知，显示破折号，不当 0。
 */
export function useMoney() {
  const { locale, t } = useI18n()
  const tag = intlTag(locale)
  return useMemo(() => {
    const petals = (units: number | null | undefined) => (units === null || units === undefined ? '—' : t(G.common.petalsValue, { n: fmtPetals(units, tag) }))
    const usd = (units: number | null | undefined, digits = 4) => (units === null || units === undefined ? '—' : fmtUsd(units, tag, digits))
    return { petals, usd, tag }
  }, [t, tag])
}

// ------------------------------------------------------------------ 定时器

/** 带清理的定时器；delay 为 null 时不运行。回调总是读到最新的闭包。 */
export function useInterval(fn: () => void, delay: number | null): void {
  const ref = useRef(fn)
  ref.current = fn
  useEffect(() => {
    if (delay === null) return
    const id = window.setInterval(() => ref.current(), delay)
    return () => window.clearInterval(id)
  }, [delay])
}

// ------------------------------------------------------------------ 输入框

/** 带放大镜的筛选输入框；回车触发 onEnter，输入法组合中的回车是选词，不触发。 */
export function SearchInput({
  width,
  onEnter,
  ...rest
}: InputHTMLAttributes<HTMLInputElement> & { width: number; onEnter?: () => void }) {
  return (
    <div style={{ position: 'relative' }}>
      <IconSearch
        size={14}
        style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-tertiary)', pointerEvents: 'none' }}
      />
      <input
        {...rest}
        type="text"
        style={{ ...fieldStyle, padding: '8px 10px 8px 32px', outline: 'none', width }}
        onKeyDown={(e) => {
          rest.onKeyDown?.(e)
          if (e.key === 'Enter' && !e.nativeEvent.isComposing) onEnter?.()
        }}
      />
    </div>
  )
}

// ------------------------------------------------------------------ 弹窗

/**
 * Alice 的弹窗外壳：.modal-mask.open > .modal。
 * 约束：有输入框的弹窗（导出对话框）不要开 dismissOnMask，避免点空白处丢掉已填内容；Esc 总是可关。
 */
export function AliceModal({
  open,
  onClose,
  title,
  width,
  dismissOnMask = false,
  children,
}: {
  open: boolean
  onClose: () => void
  title: ReactNode
  width?: number | string
  dismissOnMask?: boolean
  children: ReactNode
}) {
  const closeRef = useRef(onClose)
  closeRef.current = onClose
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeRef.current()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open])
  if (!open) return null
  const style: CSSProperties = { width: width ?? 520, maxWidth: '96vw' }
  return createPortal(
    <div
      className="modal-mask open"
      style={{ zIndex: 9000 }}
      onMouseDown={(e) => {
        if (dismissOnMask && e.target === e.currentTarget) onClose()
      }}
    >
      <div className="modal" role="dialog" aria-modal="true" style={style}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <div className="modal-title" style={{ marginBottom: 0 }}>
            {title}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="close"
            style={{ padding: '4px 8px', border: 'none', background: 'none', cursor: 'pointer', color: 'var(--text-tertiary)', fontSize: 18 }}
          >
            ✕
          </button>
        </div>
        {children}
      </div>
    </div>,
    document.body,
  )
}

export interface ExportParams {
  startDate: string
  endDate: string
  maxRows: number
  withBodies: boolean
}

const ROW_OPTIONS = [100, 500, 1000, 3000, 5000, 10000]

/**
 * 导出对话框：起止日期、最大条数、可选「带正文」。
 * 约束：onSubmit 抛错时错误显示由调用方负责（toast），对话框保持打开；成功后由调用方关闭。
 */
export function ExportDialog({
  open,
  title,
  note,
  withBodies,
  onSubmit,
  onClose,
}: {
  open: boolean
  title: ReactNode
  note?: ReactNode
  /** 提供时显示「同时导出正文」勾选，及其提示。 */
  withBodies?: boolean
  onSubmit: (p: ExportParams) => Promise<void>
  onClose: () => void
}) {
  const { t } = useI18n()
  const [start, setStart] = useState('')
  const [end, setEnd] = useState('')
  const [rows, setRows] = useState(1000)
  const [bodies, setBodies] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  useEffect(() => {
    if (!open) return
    const today = todayLocal()
    setStart(addDays(today, -7))
    setEnd(today)
    setError('')
    setBodies(false)
  }, [open])

  const submit = async () => {
    if (!start || !end) return setError(t(G.exportDialog.needRange))
    if (start > end) return setError(t(G.exportDialog.badRange))
    setError('')
    setBusy(true)
    try {
      await onSubmit({ startDate: start, endDate: end, maxRows: rows, withBodies: bodies })
    } finally {
      setBusy(false)
    }
  }
  const label: CSSProperties = { fontSize: 13, color: 'var(--text-tertiary)', display: 'block', marginBottom: 4 }
  const input: CSSProperties = { ...fieldStyle, width: '100%', boxSizing: 'border-box' }
  return (
    <AliceModal open={open} onClose={() => !busy && onClose()} title={title} width={420}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {note && <div style={{ fontSize: 11, color: 'var(--text-tertiary)', lineHeight: 1.65 }}>{note}</div>}
        <div>
          <label style={label}>{t(G.exportDialog.startDate)}</label>
          <input type="date" value={start} onChange={(e) => setStart(e.target.value)} style={input} />
        </div>
        <div>
          <label style={label}>{t(G.exportDialog.endDate)}</label>
          <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} style={input} />
        </div>
        <div>
          <label style={label}>{t(G.exportDialog.maxRows)}</label>
          <select value={rows} onChange={(e) => setRows(Number(e.target.value))} style={{ ...input, cursor: 'pointer' }}>
            {ROW_OPTIONS.map((n) => (
              <option key={n} value={n}>
                {t(G.exportDialog.rows, { n })}
              </option>
            ))}
          </select>
        </div>
        {withBodies && (
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 13, cursor: 'pointer' }}>
            <input type="checkbox" checked={bodies} onChange={(e) => setBodies(e.target.checked)} style={{ marginTop: 3 }} />
            <span>
              {t(G.exportDialog.withBodies)}
              <span style={{ display: 'block', fontSize: 11, color: 'var(--text-tertiary)', marginTop: 2 }}>{t(G.exportDialog.withBodiesHint)}</span>
            </span>
          </label>
        )}
        {error && <div style={{ fontSize: 12, color: 'var(--error-text)' }}>{error}</div>}
      </div>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20 }}>
        <button type="button" className="btn btn-secondary btn-sm" onClick={onClose} disabled={busy}>
          {t(G.common.cancel)}
        </button>
        <button type="button" className="btn btn-primary btn-sm" onClick={submit} disabled={busy}>
          {busy ? t(G.exportDialog.working) : t(G.exportDialog.submit)}
        </button>
      </div>
    </AliceModal>
  )
}

// ------------------------------------------------------------------ 图表

/**
 * Chart.js 画布。config 变化时销毁重建（与 Alice 一致，数据量小，重建比增量更新简单可靠），卸载时销毁。
 * 约束：调用方用 useMemo 稳定 config，否则每次渲染都会重建。
 */
export function ChartCanvas({ config }: { config: ChartConfiguration }) {
  const ref = useRef<HTMLCanvasElement>(null)
  useEffect(() => {
    if (!ref.current) return
    const chart = new Chart(ref.current, config)
    return () => chart.destroy()
  }, [config])
  return <canvas ref={ref} />
}

/** Alice 的图表公共选项（图例右上、坐标轴不画竖线、浅色网格）。 */
export function baseChartOptions() {
  return {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { position: 'top' as const, align: 'end' as const, labels: { font: { size: 10 }, usePointStyle: true, pointStyle: 'circle', boxWidth: 6 } } },
    scales: {
      x: { grid: { display: false }, border: { display: false }, ticks: { font: { size: 10 }, color: '#9ca3af' } },
      y: { beginAtZero: true, border: { display: false }, grid: { color: 'rgba(148,163,184,0.12)' }, ticks: { font: { size: 10 }, color: '#9ca3af' } },
    },
    interaction: { intersect: false, mode: 'index' as const },
  }
}

// ------------------------------------------------------------------ 日期时间选择器

interface Parts {
  year: number
  month: number
  day: number
  hour: number
  minute: number
  second: number
}

function nowParts(): Parts {
  const d = new Date()
  return { year: d.getFullYear(), month: d.getMonth() + 1, day: d.getDate(), hour: d.getHours(), minute: d.getMinutes(), second: d.getSeconds() }
}

function parseParts(value: string): Parts | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?$/.exec(value.trim())
  if (!m) return null
  return { year: +m[1], month: +m[2], day: +m[3], hour: +m[4], minute: +m[5], second: +(m[6] ?? 0) }
}

const toValue = (p: Parts) => `${p.year}-${pad(p.month)}-${pad(p.day)}T${pad(p.hour)}:${pad(p.minute)}:${pad(p.second)}`

const PLACEHOLDER = 'YYYY-MM-DD HH:mm:ss'

/**
 * Alice 的日期时间选择器（24 小时制，精确到秒）。
 * 约束：popup 用 position: fixed 定位到按钮下方（放不下时翻到上方），窗口缩放、页面滚动、点击外部都会关闭；
 *   时分秒下拉自身滚动不能触发关闭，所以 scroll 监听要排除 popup 内部。
 */
export function DateTimeField({
  value,
  onChange,
  prefix,
  ariaLabel,
}: {
  value: string
  onChange: (value: string) => void
  prefix: ReactNode
  ariaLabel: string
}) {
  const { t } = useI18n()
  const [open, setOpen] = useState(false)
  const [draft, setDraft] = useState<Parts>(nowParts)
  const [view, setView] = useState({ year: 0, month: 0 })
  const btn = useRef<HTMLButtonElement>(null)
  const popup = useRef<HTMLDivElement>(null)
  const [pos, setPos] = useState({ left: 0, top: 0 })
  const id = useId()

  const close = useCallback(() => setOpen(false), [])

  const toggle = () => {
    if (open) return close()
    const p = parseParts(value) ?? nowParts()
    setDraft(p)
    setView({ year: p.year, month: p.month })
    setOpen(true)
  }

  useLayoutEffect(() => {
    if (!open || !btn.current || !popup.current) return
    const r = btn.current.getBoundingClientRect()
    const margin = 10
    const w = popup.current.offsetWidth
    const h = popup.current.offsetHeight
    let top = r.bottom + 6
    if (top + h > window.innerHeight - margin) top = Math.max(margin, r.top - h - 6)
    setPos({ left: Math.max(margin, Math.min(r.left, window.innerWidth - w - margin)), top })
  }, [open, view.year, view.month])

  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node
      if (popup.current?.contains(target) || btn.current?.contains(target)) return
      close()
    }
    const onScroll = (e: Event) => {
      if (e.target instanceof Node && popup.current?.contains(e.target)) return
      close()
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close()
    }
    document.addEventListener('mousedown', onDown)
    window.addEventListener('resize', close)
    window.addEventListener('scroll', onScroll, true)
    window.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      window.removeEventListener('resize', close)
      window.removeEventListener('scroll', onScroll, true)
      window.removeEventListener('keydown', onKey)
    }
  }, [open, close])

  const shown = parseParts(value)
  const { year, month } = view
  const firstWeekday = new Date(year, month - 1, 1).getDay()
  const daysInMonth = new Date(year, month, 0).getDate()
  const today = nowParts()
  const weekdays = t(G.datetime.weekdays).split(',')
  const shift = (delta: number) => {
    const d = new Date(year, month - 1 + delta, 1)
    setView({ year: d.getFullYear(), month: d.getMonth() + 1 })
  }
  const setPart = (part: 'hour' | 'minute' | 'second', v: string) => setDraft((d) => ({ ...d, [part]: Number(v) }))
  const options = (max: number) =>
    Array.from({ length: max }, (_, i) => (
      <option key={i} value={i}>
        {pad(i)}
      </option>
    ))

  return (
    <div>
      <button
        ref={btn}
        type="button"
        className={`log-datetime-field${value ? ' has-value' : ''}`}
        aria-label={ariaLabel}
        aria-expanded={open}
        aria-controls={id}
        onClick={toggle}
      >
        <span className="log-datetime-prefix">{prefix}</span>
        <span className="log-datetime-value">{shown ? value.replace('T', ' ') : PLACEHOLDER}</span>
        <IconCalendar size={15} />
      </button>
      {open &&
        createPortal(
          <div id={id} ref={popup} className="log-datetime-popup" style={{ left: pos.left, top: pos.top }}>
            <div className="log-dtp-head">
              <button type="button" className="log-dtp-nav" onClick={() => shift(-1)} aria-label={t(G.datetime.prevMonth)}>
                ‹
              </button>
              <div className="log-dtp-title">{t(G.datetime.monthTitle, { y: year, m: pad(month) })}</div>
              <button type="button" className="log-dtp-nav" onClick={() => shift(1)} aria-label={t(G.datetime.nextMonth)}>
                ›
              </button>
            </div>
            <div className="log-dtp-week">
              {weekdays.map((w) => (
                <span key={w}>{w}</span>
              ))}
            </div>
            <div className="log-dtp-days">
              {Array.from({ length: firstWeekday }, (_, i) => (
                <span key={`b${i}`} className="log-dtp-blank" />
              ))}
              {Array.from({ length: daysInMonth }, (_, i) => {
                const day = i + 1
                const selected = draft.year === year && draft.month === month && draft.day === day
                const isToday = today.year === year && today.month === month && today.day === day
                return (
                  <button
                    key={day}
                    type="button"
                    className={`log-dtp-day${selected ? ' selected' : ''}${isToday ? ' today' : ''}`}
                    onClick={() => setDraft((d) => ({ ...d, year, month, day }))}
                  >
                    {day}
                  </button>
                )
              })}
            </div>
            <div className="log-dtp-time">
              <select aria-label={t(G.datetime.hour)} value={draft.hour} onChange={(e) => setPart('hour', e.target.value)}>
                {options(24)}
              </select>
              <span>:</span>
              <select aria-label={t(G.datetime.minute)} value={draft.minute} onChange={(e) => setPart('minute', e.target.value)}>
                {options(60)}
              </select>
              <span>:</span>
              <select aria-label={t(G.datetime.second)} value={draft.second} onChange={(e) => setPart('second', e.target.value)}>
                {options(60)}
              </select>
            </div>
            <div className="log-dtp-actions">
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => {
                  onChange('')
                  close()
                }}
              >
                {t(G.datetime.clear)}
              </button>
              <div className="log-dtp-actions-right">
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() => {
                    const p = nowParts()
                    setDraft(p)
                    setView({ year: p.year, month: p.month })
                  }}
                >
                  {t(G.datetime.now)}
                </button>
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={() => {
                    onChange(toValue(draft))
                    close()
                  }}
                >
                  {t(G.datetime.ok)}
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  )
}
